import { memo, ReactNode } from 'react';

import styled, { css, useTheme } from '@mint-lib/styled';

import { Box, Icon, TableBodyCell } from '@myn/mui';

import {
  BUILDER_ROW_TYPES,
  DEFAULT_FIELD_WIDTH,
  MIN_COLUMN_WIDTH,
  ROW_HEIGHT,
} from '../../../../../constants/campaignBuilder.js';
import { Columns } from '../../../../../constants/myCampaigns.js';
import { useGetBudgetRows } from '../../../../../hooks/useBudgetRows.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../types.js';
import { getNewMediaRows } from './utils.js';

const TILE_DEPTH = 2;

type Props = {
  cell: {
    column: BuilderTableColumn;
    render: (type: string, cellProps: { cellColor: string }) => ReactNode;
  };
  row: BuilderTableRow;
};

const TableBodyItem = memo(({ cell, row }: Props) => {
  const [budgetRows, setBudgetRows] = useGetBudgetRows();
  const themeV2 = useTheme();

  const { type: rowType, scale, isPredictivePlanningMode } = row.original;
  const isExpander = cell.column.id === Columns.EXPANDER;
  const isSticky = cell.column.id === Columns.ACTIONS_MENU;
  const isBudgetColumns = cell.column.id === Columns.BUDGET;
  const isActionsMenu = cell.column.id === Columns.ACTIONS_MENU;
  const isMask = cell.column.id === Columns.MASK;
  const isLocked = !!budgetRows?.[row.id]?.isLocked;
  const isTotal = rowType === BUILDER_ROW_TYPES.TOTAL;
  const isLockButtonShown =
    isBudgetColumns && isPredictivePlanningMode && !isTotal;
  const height = ROW_HEIGHT;
  const cellColor =
    row.depth === TILE_DEPTH
      ? themeV2.palette.text.primary
      : themeV2.palette.text.secondary;
  const columnWidth =
    Number(cell.column.width) < MIN_COLUMN_WIDTH
      ? MIN_COLUMN_WIDTH
      : Number(cell.column.width);

  const handleClick = () => {
    if (!budgetRows) {
      return;
    }
    const rows = getNewMediaRows(budgetRows, row);
    setBudgetRows(rows);
  };
  const cellProps = {
    cellColor,
  };

  return (
    <Td
      isSticky={isSticky}
      isExpander={isExpander}
      isMask={isMask}
      isActionsMenu={isActionsMenu}
      width={columnWidth}
      height={height * scale}
      isTotal={isTotal}
      $scale={scale}
      $isPredictive={isPredictivePlanningMode}
      isBugdetColumn={isBudgetColumns}
    >
      {isLockButtonShown && (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <PinWrapper onClick={handleClick} $width={columnWidth * scale}>
            <PinContainer $scale={scale} isLocked={isLocked}>
              <Icon
                component="Pin"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 16 * scale,
                  height: 16 * scale,
                  rotate: isLocked ? '0deg' : '-45deg',
                  transition: 'rotate 0.2s',
                }}
              />
            </PinContainer>
          </PinWrapper>
        </Box>
      )}

      {row.original.id || isExpander ? cell.render('Cell', cellProps) : null}
    </Td>
  );
});

const Td = styled(TableBodyCell)<{
  isSticky: boolean;
  isActionsMenu: boolean;
  width: number;
  isExpander: boolean;
  isMask: boolean;
  height: number;
  isTotal: boolean;
  $scale: number;
  $isPredictive: boolean;
  isBugdetColumn: boolean;
}>`
  padding: 0;
  vertical-align: middle;
  background-color: ${({ theme }) => theme.palette.ui.primary};
  min-height: unset;
  & > div {
    min-height: unset;
  }

  ${({ isTotal, $isPredictive, theme }) =>
    isTotal &&
    css`
      // temporary nested solution until redesign ff exists
      background-color: ${$isPredictive
        ? theme.palette.background.primary
        : theme.palette.ui.secondary};
    `}

  ${({ isExpander, isTotal, theme: newTheme }) =>
    isExpander &&
    css`
      padding-left: 0;
      padding-right: 0;
      border-top: ${isTotal
        ? `1px solid ${newTheme.palette.ui.tertiary}`
        : 'unset'};
      border-right: 4px solid rgba(47, 57, 65, 0.08);
      border-left: unset;
      position: sticky;
      left: 0;
      z-index: 1;
    `}
  ${({ isBugdetColumn }) =>
    isBugdetColumn &&
    css`
      padding-left: 0;
      padding-right: 0;
    `}

  ${({ width, isMask, isActionsMenu, $scale }) => {
    if (isActionsMenu) {
      return css`
        position: sticky;
        left: auto;
        right: 0;
        width: 0;
        height: 100%;
        opacity: 0;
        visibility: hidden;
      `;
    }
    if (isMask) {
      return css`
        width: 100%;
        min-width: ${DEFAULT_FIELD_WIDTH * $scale}px;
      `;
    }
    return width
      ? css`
          width: ${width * $scale}px;
          min-width: ${width * $scale}px;
          max-width: ${width * $scale}px;
        `
      : css`
          width: 208px;
          min-width: 208px;
          max-width: 208px;
        `;
  }}
`;

const PinWrapper = styled('div')<{
  $width: number;
}>`
  position: absolute;
  width: ${({ $width }) => $width}px;
  height: 100%;
  overflow: hidden;

  :hover div:nth-child(1) {
    visibility: visible !important;
  }
`;

const PinContainer = styled('div')<{
  isLocked: boolean;
  $scale: number;
}>`
  ${({ isLocked, $scale, theme }) => css`
    border-top: ${32 * $scale}px solid ${theme.palette.silver.main};
    visibility: ${isLocked ? 'visible' : 'hidden'};
  `}

  width: 0;
  transition: border-top 0.3s;
  ${({ $scale }) => css`
    border-right: ${32 * $scale}px solid transparent;
  `}
`;

export default TableBodyItem;
